import React, { useMemo } from "react";
import {
  Stack,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";

import useLocalization from "../../hooks/useLocalization";

import Select from "../elements/Select";
import SurveyImageCapture from "./SurveyImageCapture";

const SurveyEntry_Distribution = ({
  question,
  value,
  onChange,
  disabled,
  testMode = false,
}) => {
  const { getLabel, distribution } = useLocalization();

  // Parse value from JSON string to object
  const parsedValue = useMemo(() => {
    try {
      if (typeof value === "string") {
        return JSON.parse(value);
      }
      return value || {};
    } catch (err) {
      console.error("Error parsing distribution value:", err);
      return {};
    }
  }, [value]);

  // Determine which follow-up question type based on action
  const getFollowUpQuestionType = (action) => {
    if (action === "noTag-ProductOnShelf") {
      return "why_tag_not_placed";
    }
    if (action === "noTag-NoProduct-ProductOrdered") {
      return "why_tag_not_on_shelf";
    }
    if (action === "planogramNotCompleted") {
      return "why_not_on_pog";
    }
    return null;
  };

  // Actions that require photos (1-4)
  const photoActions = [
    "placedTagandProduct",
    "tag-NoBackroomAccess",
    "tag-ProductOrdered",
    "noTag-ProductOnShelf",
  ];

  // Actions that require follow-up questions (4, 5, 6)
  const followUpActions = [
    "noTag-ProductOnShelf",
    "noTag-NoProduct-ProductOrdered",
    "planogramNotCompleted",
  ];

  const handleChange = (field, newValue) => {
    let valueToStore = newValue;

    // Convert string IDs "1"/"0" to numeric for storage
    if (field === "availableForSale") {
      valueToStore = newValue === "1" ? 1 : newValue === "0" ? 0 : newValue;
    }

    const updated = { ...parsedValue, [field]: valueToStore };

    // Clear dependent fields when action changes
    if (field === "action") {
      // Clear action photo if switching to action that doesn't need it
      if (!photoActions.includes(newValue)) {
        delete updated.actionPhoto;
      }
      // Clear follow-up if switching to action that doesn't need it
      if (!followUpActions.includes(newValue)) {
        delete updated.followUp;
      }
    }

    // Clear follow-up if availableForSale changes to YES
    if (field === "availableForSale" && newValue === 1) {
      delete updated.action;
      delete updated.actionPhoto;
      delete updated.followUp;
    }

    onChange(JSON.stringify(updated));
  };

  // Convert numeric availableForSale to string ID for Select component
  const getAvailableForSaleValue = () => {
    const val = parsedValue?.availableForSale;
    if (val === 1 || val === "1") {
      return "1";
    }
    if (val === 0 || val === "0") {
      return "0";
    }
    return "";
  };

  const selectValue = getAvailableForSaleValue();
  const isYes = parsedValue?.availableForSale === 1;
  const isNo = parsedValue?.availableForSale === 0;
  const currentAction = parsedValue?.action;
  const followUpType = currentAction
    ? getFollowUpQuestionType(currentAction)
    : null;
  const needsActionPhoto =
    currentAction && photoActions.includes(currentAction);
  const needsFollowUp =
    currentAction && followUpActions.includes(currentAction);

  return (
    <Stack
      spacing={2}
      sx={{ marginTop: "8px" }}
    >
      {/* STEP 1: Available For Sale Entry Question (ALWAYS VISIBLE) */}
      <Box>
        <Select
          size="small"
          label={distribution.questionText.availableForSale}
          value={selectValue}
          onChange={(newValue) => handleChange("availableForSale", newValue)}
          items={distribution.availableForSale}
          disabled={disabled}
        />
      </Box>

      {/* STEP 2A: If YES - Show Entry Photo Only */}
      {isYes && (
        <Box>
          <SurveyImageCapture
            value={parsedValue?.entryPhoto}
            onChange={(newValue) => handleChange("entryPhoto", newValue)}
            imageType="standard"
            minImages={1}
            maxImages={1}
            resolution="high"
            disabled={disabled}
            label="Entry Photo"
          />
        </Box>
      )}

      {/* STEP 2B: If NO - Show Action Selection + Conditional Branches */}
      {isNo && (
        <Stack spacing={2}>
          {/* Action Dropdown */}
          <Box>
            <Select
              size="small"
              label={distribution.questionText.actionTaken}
              value={currentAction || ""}
              items={distribution.actionTaken}
              onChange={(newValue) => handleChange("action", newValue)}
              disabled={disabled}
            />
          </Box>

          {/* STEP 3: Action-Specific Branches */}
          {currentAction && (
            <Stack spacing={2}>
              {/* Branch: Actions 1-4 require ACTION PHOTO BEFORE follow-up question */}
              {needsActionPhoto && (
                <Box>
                  <SurveyImageCapture
                    value={parsedValue?.actionPhoto}
                    onChange={(newValue) =>
                      handleChange("actionPhoto", newValue)
                    }
                    imageType="standard"
                    minImages={1}
                    maxImages={1}
                    resolution="high"
                    disabled={disabled}
                    label="Action Photo"
                  />
                </Box>
              )}

              {/* STEP 4: Follow-up Question (for Actions 4, 5, 6) */}
              {needsFollowUp && followUpType && (
                <Box>
                  <div style={{ marginBottom: "12px" }}>
                    <strong>
                      {
                        distribution.questionText.followUpQuestions[
                          followUpType
                        ]
                      }
                    </strong>
                  </div>

                  {/* Radio Group for follow-up options */}
                  <RadioGroup
                    value={parsedValue?.followUp?.answer || ""}
                    onChange={(e) => {
                      const newFollowUp = {
                        question: followUpType,
                        answer: e.target.value,
                        comment:
                          e.target.value === "other"
                            ? parsedValue?.followUp?.comment || ""
                            : null,
                      };
                      handleChange("followUp", newFollowUp);
                    }}
                    disabled={disabled}
                  >
                    {distribution.followUpOptions[followUpType]?.map(
                      (option) => (
                        <FormControlLabel
                          key={option.id}
                          value={option.id}
                          control={<Radio />}
                          label={option.name}
                        />
                      ),
                    )}
                  </RadioGroup>

                  {/* Conditional Comment Field - Only if "other" is selected */}
                  {parsedValue?.followUp?.answer === "other" && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Please provide details"
                      value={parsedValue?.followUp?.comment || ""}
                      onChange={(e) => {
                        const newFollowUp = {
                          ...parsedValue.followUp,
                          comment: e.target.value,
                        };
                        handleChange("followUp", newFollowUp);
                      }}
                      disabled={disabled}
                      margin="normal"
                    />
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default SurveyEntry_Distribution;
